import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { X, ChevronLeft, ShieldCheck, Heart, Check } from "lucide-react";
import toast from "react-hot-toast";
import SummaryApi from "../common";
import { allQuestions } from "./Quizquestionsdata";
import RecommendedProducts from "./RecommendedProducts";

const Questionnaire = ({ isOpen, onClose, onCompleteSuccess }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [userId, setUserId] = useState(null);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false); // NEW: For smooth transition
  const [attemptedCount, setAttemptedCount] = useState(0);
  const [showFinalThankYou, setShowFinalThankYou] = useState(false);
  const [masterData, setMasterData] = useState({
    products: [],
    packages: [],
    loading: false,
  });
  const syncLock = useRef(false);
  const guestIds = [1, 2, 4, 5, 15, 16];

  // Helper to handle closing and resetting internal local state
  const handleInternalClose = () => {
    setShowResults(false);
    setShowFinalThankYou(false);
    setIsAnalyzing(false);
    setStep(0);
    onClose();
  };

  const getUserData = useCallback(() => {
    const sessionData = localStorage.getItem("user");
    if (!sessionData) return null;
    try {
      return JSON.parse(sessionData);
    } catch {
      return null;
    }
  }, []);

  const refreshSessionData = async (uid) => {
    try {
      const res = await fetch(SummaryApi.userDetailsById.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: uid }),
      });
      const result = await res.json();
      if (result.status && result.data) {
        const d = result.data;
        const filteredUser = {
          user_id: d.user_id,
          is_quiz: d.is_quiz,
          is_subscriber: d.is_subscriber,
          account_type: d.account_type,
          total_questions_attempted: d.total_questions_attempted,
        };
        localStorage.setItem("user", JSON.stringify(filteredUser));
        return filteredUser;
      }
    } catch (err) {
      console.error("Failed to refresh session:", err);
    }
    return null;
  };

  useEffect(() => {
    const syncQuizData = async () => {
      const userData = getUserData();
      const storedResults = localStorage.getItem("quiz_results");

      if (
        userData?.user_id &&
        Number(userData?.total_questions_attempted || 0) === 0 &&
        storedResults &&
        !syncLock.current
      ) {
        syncLock.current = true;
        try {
          const quizDataArray = JSON.parse(storedResults);

          // --- FIX START: Map results back to the 'answers' state ---
          const syncedAnswers = {};
          quizDataArray.forEach((item) => {
            // Find the original question ID by matching the text
            const questionMatch = allQuestions.find(
              (q) => q.q === item.question,
            );
            if (questionMatch) {
              // Convert comma-separated string back to array if it was a multi-select
              if (questionMatch.type === "multi") {
                syncedAnswers[questionMatch.id] = item.answer.split(", ");
              } else {
                syncedAnswers[questionMatch.id] = item.answer;
              }
            }
          });
          setAnswers(syncedAnswers); // Update the state so RecommendedProducts has data
          // --- FIX END ---

          // 1. Upload to API
          await Promise.all(
            quizDataArray.map((item) =>
              fetch(SummaryApi.quiz_create.url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  user_id: userData.user_id,
                  question: item.question,
                  answer: item.answer,
                }),
              }),
            ),
          );

          // 2. Update Status
          await fetch(SummaryApi.updateQuizStatus.url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_id: userData.user_id, is_quiz: 1 }),
          });

          localStorage.removeItem("quiz_results");
          await refreshSessionData(userData.user_id);

          setShowResults(true); // Now this will show data because setAnswers was called

          if (onCompleteSuccess) onCompleteSuccess();
          toast.success("Syncing your cycle profile...", { icon: "🔄" });
        } catch (error) {
          console.error("Sync Error:", error);
        } finally {
          syncLock.current = false;
        }
      }
    };
    if (isOpen) syncQuizData();
  }, [isOpen, onCompleteSuccess, getUserData]);

  const activeQuestions = useMemo(() => {
    // Use the reactive state 'attemptedCount' instead of direct localStorage reading
    if (attemptedCount < 6) {
      return allQuestions.filter((q) => guestIds.includes(q.id));
    }

    if (attemptedCount >= 6 && attemptedCount < 16) {
      return allQuestions.filter((q) => !guestIds.includes(q.id));
    }

    return [];
  }, [attemptedCount, userId]); // Re-calculates when attemptedCount changes
  const hasFetched = useRef(false);
  const fetchMasterData = useCallback(async () => {
    // Guard clause: if we already have data or are already fetching, stop.
    if (hasFetched.current || masterData.products.length > 0) return;

    hasFetched.current = true; // Mark as started
    setMasterData((prev) => ({ ...prev, loading: true }));

    try {
      const [prodRes, pkgRes] = await Promise.all([
        fetch(SummaryApi.getActiveProduct.url),
        fetch(SummaryApi.getActivePackage.url),
      ]);
      const prodData = await prodRes.json();
      const pkgData = await pkgRes.json();
      setMasterData({
        products: prodData.data || [],
        packages: pkgData.data || [],
        loading: false,
      });
    } catch (error) {
      hasFetched.current = false; // Reset on error so we can retry
      setMasterData((prev) => ({ ...prev, loading: false }));
    }
  }, [masterData.products.length]);
  useEffect(() => {
    if (isOpen) {
      fetchMasterData();
      const userData = getUserData();
      const count = Number(userData?.total_questions_attempted || 0);
      setUserId(userData?.user_id || null);
      setAttemptedCount(count);

      if (count >= 16) {
        setShowResults(false);
        setShowFinalThankYou(true);
      } else if (count >= 6) {
        // If they already did phase 1, don't show results immediately on open
        // unless they literally just finished it.
        setShowResults(false);
        setStep(1);
      }
    }
  }, [isOpen]);

  const currentQuestion = activeQuestions[step - 1];
  const totalSteps = activeQuestions.length;

  const hasAnswer = useMemo(() => {
    if (!currentQuestion) return false;
    const ans = answers[currentQuestion.id];
    if (Array.isArray(ans)) return ans.length > 0;
    return !!ans;
  }, [answers, currentQuestion]);

  const handleOptionClick = (optionLabel) => {
    const qId = currentQuestion.id;
    if (currentQuestion.type === "single") {
      setAnswers({ ...answers, [qId]: optionLabel });
      setTimeout(() => {
        if (step < totalSteps) setStep(step + 1);
        else handleComplete();
      }, 300);
    } else {
      const currentSelections = answers[qId] || [];
      const newSelections = currentSelections.includes(optionLabel)
        ? currentSelections.filter((i) => i !== optionLabel)
        : [...currentSelections, optionLabel];
      setAnswers({ ...answers, [qId]: newSelections });
    }
  };

  // Inside Questionnaire.js

  const handleComplete = async () => {
    const formattedResults = activeQuestions.map((q) => {
      const rawAnswer = answers[q.id] || "Skipped";
      return {
        question: q.q,
        answer: Array.isArray(rawAnswer)
          ? rawAnswer.join(", ")
          : String(rawAnswer),
      };
    });

    try {
      setIsAnalyzing(true);
      const attemptedBefore = attemptedCount;

      if (userId) {
        await Promise.all(
          formattedResults.map((item) =>
            fetch(SummaryApi.quiz_create.url, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                user_id: userId,
                question: item.question,
                answer: item.answer,
              }),
            }),
          ),
        );

        await fetch(SummaryApi.updateQuizStatus.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId, is_quiz: 1 }),
        });

        const newTotal = attemptedBefore + formattedResults.length;

        // Manual storage update
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const u = JSON.parse(userStr);
          u.total_questions_attempted = newTotal;
          u.is_quiz = 1;
          localStorage.setItem("user", JSON.stringify(u));
        }

        setAttemptedCount(newTotal);
        await refreshSessionData(userId);

        setTimeout(() => {
          setIsAnalyzing(false);
          if (attemptedBefore < 6) {
            setShowResults(true);
            toast.success("Phase 1 Analysis Completed!", { icon: "🌸" });
          } else {
            setShowFinalThankYou(true);
          }
          if (onCompleteSuccess) onCompleteSuccess();
        }, 2000);
      } else {
        // Guest: Store in localStorage for sync later
   sessionStorage.setItem("quiz_results", JSON.stringify(formattedResults));
        setTimeout(() => {
          setIsAnalyzing(false);
          setShowResults(true);
          if (onCompleteSuccess) onCompleteSuccess();
        }, 2000);
      }
    } catch (error) {
      console.error(error);
      setIsAnalyzing(false);
      toast.error("Failed to save progress.");
    }
  };

  // FINAL GUARD: Only hide if the modal is closed OR it was already finished before we clicked it.
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 font-sans">
      <div
        className={`bg-white w-full transition-all duration-500 ease-in-out rounded-[2.5rem] shadow-2xl overflow-hidden ${
          showResults ? "max-w-4xl" : "max-w-lg"
        }`}
      >
        <div className="bg-pink-50 px-8 py-6 flex justify-between items-center border-b border-pink-100">
          <div>
            <h3 className="font-bold text-pink-800 flex items-center gap-2">
              <Heart size={18} className="fill-pink-500 text-pink-500" /> Cycle
              Personalization
            </h3>
            {!showResults && (
              <p className="text-[10px] font-bold text-pink-400 uppercase mt-1">
                {step === 0 ? "Privacy First" : `Step ${step} of ${totalSteps}`}
              </p>
            )}
          </div>
          <button
            onClick={handleInternalClose}
            className="p-2 hover:bg-white rounded-full text-gray-400"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-8">
          {isAnalyzing ? (
            <div className="text-center animate-pulse">
              <div className="bg-pink-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="text-pink-500 animate-bounce" size={40} />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                Analyzing your profile...
              </h2>
            </div>
          ) : showFinalThankYou ? (
            /* NEW: THANK YOU SCREEN FOR HEALTHCARE */
            <div className="text-center py-10">
              <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <ShieldCheck className="text-green-600" size={40} />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                Thank You for Your Trust
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Your detailed health insights have been securely recorded. This
                helps our team understand your cycle better to provide 100%
                accurate care guidance.
              </p>
              <div className="bg-blue-50 p-4 rounded-2xl mb-8">
                <p className="text-sm text-blue-700 font-medium">
                  "Prioritizing your menstrual health is the first step toward
                  overall wellness."
                </p>
              </div>
              <button
                onClick={handleInternalClose}
                className="w-full py-4 bg-gray-900 text-white rounded-2xl font-bold hover:bg-black transition-all"
              >
                Close & Continue
              </button>
            </div>
          ) : showResults ? (
            <RecommendedProducts
              answers={answers}
              dbProducts={masterData.products}
              dbPackages={masterData.packages}
              loading={masterData.loading}
              onClose={handleInternalClose}
              onReset={() => {
                setShowResults(false);
                setAnswers({});
                setStep(1);
              }}
            />
          ) : step === 0 ? (
            /* Privacy Screen */
            <div className="text-center">
              <div className="bg-pink-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="text-pink-600" size={32} />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                Your Privacy Matters
              </h2>
              <p className="text-sm text-gray-600 mb-8 leading-relaxed">
                "Help us recommend products tailored to YOUR menstrual cycle."
                <br /> Your health data is encrypted and kept private.
              </p>
              <button
                onClick={() => setStep(1)}
                className="w-full py-4 bg-gray-900 text-white rounded-2xl font-bold shadow-lg hover:bg-black transition-all"
              >
                Accept & Start
              </button>
            </div>
          ) : (
            <div>
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800">
                  {currentQuestion?.q}
                </h2>
                <p className="text-sm text-gray-500">{currentQuestion?.sub}</p>
              </div>
              <div className="mb-8 min-h-[300px]">
                {currentQuestion?.type === "input" ? (
                  <input
                    type="text"
                    placeholder={currentQuestion?.placeholder}
                    value={answers[currentQuestion.id] || ""}
                    onChange={(e) =>
                      setAnswers({
                        ...answers,
                        [currentQuestion.id]: e.target.value,
                      })
                    }
                    className="w-full p-5 rounded-2xl border-2 border-pink-100 focus:border-pink-500 outline-none"
                    autoFocus
                  />
                ) : (
                  <div className="max-h-[380px] overflow-y-auto space-y-3">
                    {currentQuestion?.options?.map((opt) => {
                      const isSelected =
                        currentQuestion.type === "single"
                          ? answers[currentQuestion.id] === opt.label
                          : answers[currentQuestion.id]?.includes(opt.label);
                      return (
                        <button
                          key={opt.label}
                          onClick={() => handleOptionClick(opt.label)}
                          className={`w-full p-4 text-left rounded-2xl border-2 transition-all ${isSelected ? "border-pink-500 bg-pink-50" : "border-gray-50 hover:bg-pink-50/50"}`}
                        >
                          <div className="flex justify-between items-center w-full">
                            <div className="flex-1">
                              <p className="text-sm font-bold mb-0.5">
                                {opt.label}
                              </p>
                              {opt.note && (
                                <p
                                  className={`text-[11px] ${isSelected ? "text-pink-500" : "text-gray-400"}`}
                                >
                                  {opt.note}
                                </p>
                              )}
                            </div>
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? "bg-pink-500 border-pink-500" : "border-gray-200"}`}
                            >
                              {isSelected && (
                                <Check
                                  size={16}
                                  className="text-white stroke-[3px]"
                                />
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="w-full bg-gray-100 h-1.5 rounded-full mb-6">
                <div
                  className="bg-pink-500 h-full rounded-full transition-all duration-500"
                  style={{ width: `${(step / totalSteps) * 100}%` }}
                />
              </div>

              <div className="flex justify-between items-center">
                <button
                  onClick={() => setStep(step - 1)}
                  className="text-gray-400 font-bold flex items-center gap-1"
                >
                  <ChevronLeft size={18} /> Back
                </button>
                <button
                  onClick={() =>
                    step === totalSteps ? handleComplete() : setStep(step + 1)
                  }
                  className={`px-8 py-2.5 rounded-xl font-bold shadow-md transition-all active:scale-95 ${hasAnswer ? "bg-pink-500 text-white" : "bg-gray-100 text-gray-400"}`}
                >
                  {step === totalSteps
                    ? hasAnswer
                      ? "Finish"
                      : "Skip & Finish"
                    : hasAnswer
                      ? "Next"
                      : "Skip"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Questionnaire;
